import { CloseIcon } from "@chakra-ui/icons";
import { Box, Flex, Tag, TagLabel, Text, useToast } from "@chakra-ui/react";
import { isValidDomain, serializeRawDomain } from "@intentsify/utils";
import { Button, Modal, Upload } from "components";
import uniq from "lodash/uniq";
import * as Papa from "papaparse";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { MdUploadFile } from "react-icons/md";
import { parseCSV } from "utils";
import { QuantumDemandDomainsFile } from "./QuantumDemand.types";

const MAX_DOMAINS_COUNT = 1000;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const DOMAIN_COLUMN = "domain";

const domainsCsvDefinition = {
  domain: {
    isOptional: false,
    validator: (value: string) => {
      if (!isValidDomain(serializeRawDomain(value))) {
        return "Invalid domain";
      }
    },
  },
};

const getFirstColumnName = (csv: string) => {
  const { data } = Papa.parse<string[]>(csv, {
    preview: 1,
    skipEmptyLines: "greedy",
  });

  return data[0]?.[0]?.trim().toLowerCase();
};

type QuantumDemandDomainsUploadProps = {
  domainsFile: QuantumDemandDomainsFile | undefined;
  onDomainsFileChange: (
    domainsFile: QuantumDemandDomainsFile | undefined,
  ) => void;
};

export function QuantumDemandDomainsUpload({
  domainsFile,
  onDomainsFileChange,
}: QuantumDemandDomainsUploadProps) {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const processFile = useCallback(
    (file: File) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        const csv = event.target?.result?.toString();

        if (!csv) {
          return;
        }

        if (getFirstColumnName(csv) !== DOMAIN_COLUMN) {
          toast({
            status: "warning",
            title: `The first column of the uploaded CSV file must be named "domain".`,
            isClosable: true,
          });

          return;
        }

        const { data, errors } = await parseCSV<"domain">(
          csv,
          domainsCsvDefinition,
        );

        if (data.length > MAX_DOMAINS_COUNT) {
          toast({
            status: "warning",
            title: `Sorry, you can upload at most ${MAX_DOMAINS_COUNT} domains and your file has ${data.length} rows. Please edit your file or try a different one.`,
            duration: 1000 * 10,
            isClosable: true,
          });

          return;
        }

        if (errors.length > 0) {
          toast({
            status: "warning",
            title: `${errors.length} invalid ${
              errors.length > 1 ? "rows were" : "row was"
            } found in the uploaded CSV file`,
            description: errors
              .slice(0, 5)
              .map((error) => `Row ${error.row}: ${error.error}`)
              .join(", "),
            duration: 1000 * 10,
            isClosable: true,
          });

          return;
        }

        const domains = uniq(
          data.map((row) => serializeRawDomain(row.domain)),
        ).filter(Boolean);

        if (domains.length === 0) {
          toast({
            status: "warning",
            title: "No domains were found in the uploaded CSV file.",
            isClosable: true,
          });

          return;
        }

        onDomainsFileChange({ fileName: file.name, domains });
        setIsOpen(false);
      };

      reader.readAsText(file);
    },
    [onDomainsFileChange, toast],
  );

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    accept: { "text/csv": [".csv"] },
    onDropAccepted: ([file]) => processFile(file),
    onDropRejected: () => {
      toast({
        status: "warning",
        title: `Please upload a single CSV file of up to ${
          MAX_FILE_SIZE / 1024 / 1024
        }MB in size.`,
        isClosable: true,
      });
    },
  });

  return (
    <>
      <Flex alignItems="center" gap={2} mb={4}>
        <Button
          size="md"
          variant="outline-teal"
          leftIcon={<MdUploadFile />}
          onClick={() => setIsOpen(true)}
        >
          Upload domains
        </Button>

        {domainsFile && (
          <Tag size="lg" variant="subtle" colorScheme="teal">
            <TagLabel>
              {domainsFile.fileName} ({domainsFile.domains.length} domains)
            </TagLabel>

            <Button
              ml={2}
              size="xs"
              variant="link-teal"
              aria-label="Clear uploaded domains"
              onClick={() => onDomainsFileChange(undefined)}
            >
              <CloseIcon w={2} h={2} />
            </Button>
          </Tag>
        )}
      </Flex>

      <Modal
        title="Upload domains"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        body={
          <Box pb={4}>
            <Text fontSize="sm" mb={4}>
              Upload a CSV file with <b>Domain</b> as its first column. Each row
              should contain a single domain (e.g., &quot;google.com&quot;). You
              can upload a maximum of {MAX_DOMAINS_COUNT} domains.
            </Text>

            <Upload
              testId="quantum-demand-domains-upload-zone"
              getInputProps={getInputProps}
              getRootProps={getRootProps}
              subHeader={`CSV up to ${MAX_FILE_SIZE / 1024 / 1024}MB`}
            />
          </Box>
        }
      />
    </>
  );
}
